import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getEcho } from "../bridges/echo";
import { useCurrentUser, useWorkspaceId } from "../bridges/session";
import { useAddLegacyToast, useRefreshBilling } from "../bridges/frame";
import { useInboxLive } from "../approvals/api";
import { useOpenInbox } from "../approvals/openInbox";
import { refreshInbox } from "../visits/api";

const EVENT = "BroadcastNotificationToAdminEvent";
/** Today's notification types that are approvals: their pop-up opens "To approve" (Task 7a adopts them there). */
const APPROVAL_TYPES = new Set(["ChangedContactInfoNotification", "NewContactNotification", "NewHouseholdAddressNotification", "PendingHouseholdNotification"]);

/**
 * The frame's always-on live listeners. Mounted once, inside the new frame's inbox opener:
 * - the new bell's `NextInboxChanged` (useInboxLive: approvals and counts refresh);
 * - today's `BroadcastNotificationToAdminEvent` (a reminder, an alert, an import…): every such type is in the new
 *   panel's Updates (or "To approve"), so the new bell refreshes, today's pop-up shows, and clicking it opens the
 *   NEW panel (owner answer 8: never today's);
 * - billing is fetched once per user.
 *
 * Cleanup removes ONLY this handler (stopListening) — never leave(): the `admin.<id>` channel is
 * shared with the import-progress listener. The bridge hooks return fresh functions every render,
 * so they are read through a ref and the subscription is keyed on the primitive user id only.
 */
export const useFrameLiveEvents = (): void => {
  const user = useCurrentUser();
  const userId = user?.id;
  const ws = useWorkspaceId();
  const client = useQueryClient();
  const refreshBilling = useRefreshBilling();
  const openInbox = useOpenInbox();
  const addToast = useAddLegacyToast();
  const latest = useRef({ refreshBilling, openInbox, addToast, refresh: () => refreshInbox(client, ws) });
  latest.current = { refreshBilling, openInbox, addToast, refresh: () => refreshInbox(client, ws) };
  useInboxLive();

  useEffect(() => {
    if (!user) return;
    latest.current.refreshBilling();
  }, [user]); // like the old SideBar: a refetched user (plan/trial change) re-reads billing

  useEffect(() => {
    if (!userId) return;
    const channel = getEcho()?.private(`admin.${userId}`);
    if (!channel) return;

    const handler = (payload: unknown) => {
      const n = (payload as any)?.notification;
      if (!n) return;
      void latest.current.refresh();
      const tab = APPROVAL_TYPES.has((payload as any)?.type) ? "approve" : "updates";
      latest.current.addToast({
        time: n.created_at_formatted,
        description: n.title?.startOfTitle,
        additionalDescription: n.title?.endOfTitle,
        userName: n.title?.householdName,
        id: n.id,
        onClick: () => latest.current.openInbox?.(tab),
      });
    };

    channel.listen(EVENT, handler);
    return () => {
      channel.stopListening(EVENT, handler);
    };
  }, [userId]);
};
